import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  MenuButton,
} from '@fluentui/react-components';
import { Dismiss24Regular, DismissFilled, Edit24Regular } from '@fluentui/react-icons';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useMsal } from '@azure/msal-react';
import { yupResolver } from '@hookform/resolvers/yup';

import { TFile } from '@modules/files';
import { TReplica } from '@modules/replicas';
import { TDocument, isTDocument } from '@modules/documents';
import { TItemType, useRefetchItemStore } from '@modules/items';
import { AccessType, TPermissions } from '@modules/access';
import { UserLabel, SignInContext } from '@modules/users';
import { isTrainingTriggerDocument, useTrainingStore } from '@modules/mocks/training';

import { ToastContext } from '@shared/contexts';
import { DateLabel, ConfirmMenu, ControlledField, ButtonLoading } from '@shared/components';
import { useSearchParams } from '@shared/hooks';
import { toTimestampString } from '@shared/helpers';
import { resolvePreviousVersionId } from '@modules/items/helpers/resolveDefaultComparisonBase.helper';

import useItemScreenStore, { EItemScreenMode } from '@modules/items/stores/useItemScreenStore';
import ApprovalWorkflow from './ApprovalWorkflow';
import ApprovalStatusButton from './ApprovalStatusButton';
import ApprovalUserDialog from './ApprovalUserDialog';
import {
  APPROVAL_STATEMENTS,
  ApprovalStatus,
  TApproval,
  TApprovalPhaseKey,
  TApprovalUserDialogState,
  TUpdateApprovalDialogState,
  TViewApprovalDialogState,
} from '../types';
import { useCancelApproval, useSignApproval } from '../api';
import ViewPdfButton from './ViewPdfButton';

type TProps = {
  state?: TViewApprovalDialogState;
  setState: (state: TViewApprovalDialogState) => void;
  setUpdateApprovalDialogState: (state: TUpdateApprovalDialogState) => void;
  item: TDocument | TFile;
  itemType: TItemType;
  permissions: TPermissions;
  replica?: TReplica;
};

const useStyles = makeStyles({
  dialog: {
    maxWidth: '820px',
    backgroundColor: '#f2f2f2',
  },
  dialogContent: {
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  menuButton: {
    '& .fui-MenuButton__menuIcon': {
      fontSize: '15px',
      marginBottom: '6px',
      rotate: '90',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'unset',
    width: 'fit-content',
    marginBottom: '0',
    marginTop: '10px',
    paddingRight: '20px',
    minWidth: '350px',
  },
});

const getSchema = (isAccept: boolean) =>
  yup.object({
    note: yup.string(),
    acceptance: isAccept
      ? yup.boolean().oneOf([true], 'Please confirm this statement.').required('Please confirm this statement.')
      : yup.boolean(),
  });
type TFormData = yup.InferType<ReturnType<typeof getSchema>>;

const PHASE_STATUSES = [ApprovalStatus.AUTHORING, ApprovalStatus.REVIEWING, ApprovalStatus.APPROVING] as const;

const ViewApprovalDialog = ({
  state,
  setState,
  setUpdateApprovalDialogState,
  item,
  itemType,
  permissions,
  replica,
}: TProps) => {
  const signApproval = useSignApproval();
  const cancelApproval = useCancelApproval();
  const { user, organization } = useContext(SignInContext);
  const { show } = useContext(ToastContext);
  const { instance, accounts } = useMsal();
  const setRefetchEnabled = useRefetchItemStore((s) => s.setRefetchEnabled);
  const openAssignTraining = useTrainingStore((s) => s.openAssign);

  const approval = state?.approvalId ? item.approvals.find((a) => a._id === state.approvalId) : undefined;
  const canUpdateApproval = Boolean(approval && !approval.dateCompleted && permissions.accessRight >= AccessType.EDIT);

  const groupNameById = useMemo(
    () => new Map(organization.customGroups.map((g) => [g.id, g.name])),
    [organization.customGroups]
  );

  const [mode, setMode] = useState<'accept' | 'decline' | undefined>();
  const [isSigning, setIsSigning] = useState(false);
  const [approvalUserDialogState, setApprovalUserDialogState] = useState<TApprovalUserDialogState>();
  const isBusy = isSigning || signApproval.isPending || cancelApproval.isPending;

  const schema = useMemo(() => getSchema(mode === 'accept'), [mode]);
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues: { note: '', acceptance: false },
  });

  const toggleMode = (selectedMode: 'accept' | 'decline') => {
    if (!approval) return;
    const nextMode = mode === selectedMode ? undefined : selectedMode;
    const versioningConfig = organization.versioningConfig;
    const phase = approval.status as TApprovalPhaseKey;

    setMode(nextMode);
    reset({
      note:
        nextMode === 'accept'
          ? (versioningConfig?.acceptNoteDefaultsByPhase?.[phase] ?? versioningConfig?.acceptNoteDefault ?? '')
          : nextMode === 'decline'
            ? (versioningConfig?.declineNoteDefaultsByPhase?.[phase] ?? versioningConfig?.declineNoteDefault ?? '')
            : '',
      acceptance: false,
    });
  };

  useEffect(() => {
    // Participant changes can advance the phase while this form is open.
    setMode(undefined);
    reset({ note: '', acceptance: false });
  }, [approval?.status, reset]);

  const refSetState = useRef(setState);
  refSetState.current = setState;

  const sign = async (formData: TFormData, activeApproval: TApproval) => {
    const { approval: updatedApproval } = await signApproval.mutateAsync({
      id: item._id,
      itemType,
      approvalId: activeApproval._id,
      action: activeApproval.status as ApprovalStatus.AUTHORING | ApprovalStatus.REVIEWING | ApprovalStatus.APPROVING,
      accept: mode === 'accept',
      note: formData.note,
    });
    setMode(undefined);
    reset();

    // Training & Competency demo: when the configured training-trigger document is
    // fully approved, surface the "Assign training" modal instead of reopening the
    // approval card. No-op for every other document / organization.
    if (
      updatedApproval.status === ApprovalStatus.APPROVED &&
      isTrainingTriggerDocument(organization.mock?.training, item._id)
    ) {
      refSetState.current({ isOpen: false, approvalId: updatedApproval._id });
      openAssignTraining();
      return;
    }

    refSetState.current({ isOpen: true, approvalId: updatedApproval._id });
  };

  const { setScreenMode } = useItemScreenStore();
  const { searchParams, change } = useSearchParams();
  useEffect(() => {
    const approvalId = searchParams.get('approval');
    const matchedApproval = approvalId ? item.approvals.find((a) => a._id === approvalId) : undefined;
    if (matchedApproval) {
      setState({ isOpen: true, approvalId: matchedApproval._id });
      if (!matchedApproval.dateCompleted && isTDocument(item)) {
        if (resolvePreviousVersionId(item.versions, matchedApproval.versionId)) {
          setScreenMode(EItemScreenMode.CHANGES);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (state?.isOpen && approval) change('approval', approval._id);
    else change('approval');
  }, [state?.isOpen, approval?._id]);

  const onSubmit = async (formData: TFormData) => {
    if (!approval || isSigning || signApproval.isPending) return;
    if (!accounts[0]?.username) return show('error', 'Could not detect your signed-in account.');

    setIsSigning(true);
    setRefetchEnabled(false);
    try {
      const response = await instance.acquireTokenPopup({
        scopes: ['api://9ff2f73a-ae62-4fab-8f54-10740b607d61/DigitalSignature.Scope'],
        prompt: 'login',
        loginHint: accounts[0].username,
      });
      if (response.accessToken) {
        if (!response.account?.username || accounts[0].username !== response.account.username)
          return show('error', 'You did not reauthenticate with the correct account.');
        await sign(formData, approval);
      }
    } finally {
      setIsSigning(false);
      setRefetchEnabled(true);
    }
  };

  const handleCancel = () => {
    if (!approval || isBusy) return;
    setMode(undefined);
    cancelApproval.mutate({
      id: item._id,
      itemType,
      approvalId: approval._id,
    });
  };

  const handleUpdateClick = () => {
    if (state && approval) {
      setState({ ...state, isOpen: false });
      setUpdateApprovalDialogState({ isOpen: true, approvalId: approval._id });
    }
  };

  const classes = useStyles();

  return (
    <Dialog
      open={Boolean(state?.isOpen && approval)}
      onOpenChange={(_, data) => {
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface className={classes.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} disabled={isBusy} />
              </DialogTrigger>
            }
          >
            <p className="flex items-center space-x-1">
              <span>Approval request for v{approval?.versionId}</span>
              {canUpdateApproval && (
                <Button
                  appearance="subtle"
                  aria-label="Edit approval request"
                  icon={<Edit24Regular />}
                  onClick={handleUpdateClick}
                  disabled={isBusy}
                />
              )}
            </p>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {approval && (
              <div className="flex flex-col space-y-3 w-fit">
                <ApprovalUserDialog
                  state={approvalUserDialogState}
                  setState={setApprovalUserDialogState}
                  groupNameById={groupNameById}
                />

                <Card className={classes.card}>
                  <p className="flex items-center">
                    <span className="block w-32 flex-shrink-0">Status:</span>
                    <ApprovalStatusButton approval={approval} textOnly />
                  </p>
                  <p className="flex">
                    <span className="block w-32 flex-shrink-0">Requested by:</span>
                    <UserLabel id={approval.creator} />
                  </p>
                  {approval.cancellationUser && (
                    <p className="flex">
                      <span className="block w-32 flex-shrink-0">Canceled by:</span>
                      <UserLabel id={approval.cancellationUser} />
                    </p>
                  )}
                  {approval.dateCompleted ? (
                    <p className="flex">
                      <span className="block w-32 flex-shrink-0">
                        Timestamp of{' '}
                        {approval.status === ApprovalStatus.APPROVED
                          ? 'approval'
                          : approval.status === ApprovalStatus.DECLINED
                            ? 'rejection'
                            : 'cancellation'}
                        :
                      </span>
                      {toTimestampString(approval.dateCompleted)}
                    </p>
                  ) : (
                    <p className="flex">
                      <span className="block w-32 flex-shrink-0">Date of request:</span>
                      <DateLabel timeZone="UTC">{approval.dateOpened}</DateLabel>
                    </p>
                  )}

                  {PHASE_STATUSES.includes(approval.status as (typeof PHASE_STATUSES)[number]) && (
                    <div className="flex space-x-1">
                      {approval.phases[
                        approval.status as
                          | ApprovalStatus.AUTHORING
                          | ApprovalStatus.REVIEWING
                          | ApprovalStatus.APPROVING
                      ]?.users?.find((u) => u.user === user._id && u.status === 'PENDING') && (
                        <>
                          <MenuButton
                            appearance={mode === 'accept' ? 'primary' : 'secondary'}
                            className={classes.menuButton}
                            disabled={isBusy}
                            onClick={() => toggleMode('accept')}
                          >
                            Accept
                          </MenuButton>
                          <MenuButton
                            appearance={mode === 'decline' ? 'primary' : 'secondary'}
                            className={classes.menuButton}
                            disabled={isBusy}
                            onClick={() => toggleMode('decline')}
                          >
                            Decline
                          </MenuButton>
                        </>
                      )}
                      <ConfirmMenu onConfirm={handleCancel}>
                        <Button appearance="secondary" disabled={isBusy}>
                          Cancel
                        </Button>
                      </ConfirmMenu>
                    </div>
                  )}
                </Card>

                {!mode && (
                  <div className="h-fit w-full mt-3">
                    <ViewPdfButton item={item} approval={approval} permissions={permissions} replica={replica} />
                  </div>
                )}
              </div>
            )}

            {approval ? (
              mode ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <ControlledField
                    key={`${mode}-${approval.status}` /* Solves the freezing issue */}
                    label={mode === 'accept' ? 'Acceptance note (optional)' : 'Rejection note'}
                    name="note"
                    control={control}
                    errors={errors}
                    inputType="textarea"
                    required={mode === 'decline'}
                    placeholder=""
                    className="h-44"
                    fieldClassName="mt-3"
                  />
                  {mode === 'accept' && (
                    <ControlledField
                      label={APPROVAL_STATEMENTS[approval.status as keyof typeof APPROVAL_STATEMENTS]}
                      name="acceptance"
                      control={control}
                      errors={errors}
                      inputType="checkbox"
                      required
                      fieldClassName="mt-1"
                    />
                  )}

                  <div className="flex space-x-2 mt-3">
                    <ButtonLoading appearance="primary" type="submit" loading={isSigning || signApproval.isPending}>
                      Confirm
                    </ButtonLoading>
                    <Button icon={<DismissFilled />} onClick={() => setMode(undefined)} disabled={isBusy} />
                  </div>
                </form>
              ) : (
                <ApprovalWorkflow
                  approval={approval}
                  approvalUserDialogState={approvalUserDialogState}
                  setApprovalUserDialogState={setApprovalUserDialogState}
                  groupNameById={groupNameById}
                />
              )
            ) : null}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ViewApprovalDialog;
